<template>
  <div class="navigation">
    <div class="navigation__main" :class="{ active: isActive }">
      <ul
        v-if="isUserLoggedIn"
        :class="[
          'navigation-level',
          'navigation-level--1',
          { subactive: activeClass.topics || activeClass.contentTypes }
        ]"
      >
        <li class="navigation__item">
          <nuxt-link class="navigation__link" :to="'/'" @click="closeMenu"
            >Home</nuxt-link
          >
        </li>

        <li v-if="!isAlumni" class="navigation__item">
          <nuxt-link
            class="navigation__link"
            :to="{ name: 'pricing' }"
            @click="closeMenu"
            >Pricing</nuxt-link
          >
        </li>

        <li
          :class="[
            'navigation__item',
            { 'navigation__item--children': topics.length > 0 }
          ]"
        >
          <nuxt-link
            :class="[
              'navigation__link',
              {
                'navigation__link--children': topics.length > 0,
                active: activeClass.topics
              }
            ]"
            :to="{ name: 'topics' }"
            @click="closeMenu"
          >
            Topics
          </nuxt-link>
          <div class="navigation__link--icon" @click="subMenuToggle('topics')">
            <fa-icon :icon="['fas', 'chevron-down']" />
          </div>
          <ul
            v-if="topics"
            :class="[
              'navigation-level navigation-level--2',
              { show: activeClass.topics }
            ]"
          >
            <li
              v-for="topic in topics"
              :key="topic.id"
              class="navigation__item"
            >
              <nuxt-link
                class="navigation__link"
                :to="{ name: 'topics-slug', params: { slug: topic.slug } }"
                @click="closeMenu"
              >
                {{ topic.name }}
              </nuxt-link>
            </li>
          </ul>
        </li>

        <li
          :class="[
            'navigation__item',
            { 'navigation__item--children': contentTypes.length > 0 }
          ]"
        >
          <nuxt-link
            :class="[
              'navigation__link',
              {
                'navigation__link--children': contentTypes.length > 0,
                active: activeClass.contentTypes
              }
            ]"
            :to="{ name: 'content-types' }"
            @click="closeMenu"
          >
            Content Types
          </nuxt-link>
          <div
            class="navigation__link--icon"
            @click="subMenuToggle('contentTypes')"
          >
            <fa-icon :icon="['fas', 'chevron-down']" />
          </div>
          <ul
            v-if="contentTypes"
            :class="[
              'navigation-level navigation-level--2',
              { show: activeClass.contentTypes }
            ]"
          >
            <li
              v-for="type in contentTypes"
              :key="type.id"
              class="navigation__item"
            >
              <nuxt-link
                class="navigation__link"
                :to="{
                  name: 'content-types-slug',
                  params: { slug: type.slug }
                }"
                @click="closeMenu"
              >
                {{ type.name }}
              </nuxt-link>
            </li>
          </ul>
        </li>

        <li class="navigation__item">
          <a
            class="navigation__link"
            target="_blank"
            href="https://www.productfocus.com/product-management-training/"
            >Training</a
          >
        </li>

        <li class="navigation__item">
          <nuxt-link
            class="navigation__link"
            :to="{ name: 'faqs' }"
            @click="closeMenu"
            >FAQs</nuxt-link
          >
        </li>
      </ul>
    </div>
    <UserWidgetNav v-if="isUserLoggedIn" />
    <div v-if="isUserLoggedIn" class="navigation__mobile">
      <a
        class="navigation__mobile--link"
        :class="{ active: isActive }"
        @click.prevent="menuToggle"
      >
        <span class="navigation__icon" />
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
  const isActive = ref(false);
  const activeClass = ref({
    topics: false,
    contentTypes: false
  });

  const userStore = useUserStore();
  const pageContentStore = usePageContentStore();

  const currentUser = computed(() => userStore.user);
  const isAlumni = computed(() => {
    return currentUser.value?.is_alumni ?? false;
  });

  const contentTypes = computed(() => pageContentStore.getContentTypes);
  const topics = computed(() => pageContentStore.getTopics);
  const isUserLoggedIn = computed(() => userStore.isUserLoggedIn);

  const menuToggle = () => {
    const menuHeight = (
      document.querySelector("#site-header") as HTMLElement | null
    )?.offsetHeight;
    const innerMenu = document.querySelector(
      ".navigation__main"
    ) as HTMLElement | null;
    const bodyEl = document.querySelector("body");

    if (innerMenu && menuHeight) {
      bodyEl?.classList.toggle("active");
      innerMenu.style.top = `${menuHeight}px`;
    }
    isActive.value = !isActive.value;
  };

  const subMenuToggle = (menuId: "topics" | "contentTypes") => {
    if (menuId === "topics") {
      activeClass.value.topics = !activeClass.value.topics;
      activeClass.value.contentTypes = false;
    } else if (menuId === "contentTypes") {
      activeClass.value.contentTypes = !activeClass.value.contentTypes;
      activeClass.value.topics = false;
    }
  };

  const closeMenu = () => {
    setTimeout(() => {
      const bodyEl = document.querySelector("body");
      bodyEl?.classList.remove("active");
      isActive.value = false;
    }, 1000);
  };
</script>

<style lang="scss">
  @use "sass:color";
  @use "@/assets/scss/variables" as *;
  @use "@/assets/scss/mixins" as *;

  .navigation {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    flex-grow: 0;
    height: auto;

    @include respond-to-max("lg") {
      justify-content: flex-end;
    }

    @include respond-to-max("sm") {
      flex-grow: 2;
      justify-content: space-around;
      margin-left: 20px;
    }
  }

  .badge {
    display: inline-flex;
    background: $pforange;
    padding: 1px 4px;
    font-size: 0.675rem;
    border-radius: 3px;
    min-width: 30px;
    min-height: 20px;
    vertical-align: middle;
    place-content: center center;
    font-weight: bold;

    > span {
      display: flex;
      align-self: center;
      margin-top: -2px;
    }
  }

  .navigation__item {
    &:hover {
      .navigation-level--2 {
        pointer-events: all;
        opacity: 100%;
        display: flex;
      }
    }
  }

  .navigation__item--stacked {
    position: relative;
  }

  .navigation__link {
    transition: color ease 0.1s;
    color: $pfgreen;
    display: block;
    font-weight: 600;
    width: 100%;
    box-sizing: border-box;
    font-size: 0.9375rem;

    &:hover,
    &.nuxt-link-active,
    &.active {
      background-color: $pforange;
      color: $white;
      text-decoration: none;

      .badge {
        background-color: $pfgreen;
      }
    }
  }

  .navigation-level {
    list-style: none;
  }

  .navigation-level--1 {
    display: flex;

    &.subactive {
      > .navigation__item {
        > .navigation__link.nuxt-link-active {
          background-color: color.adjust($pforange, $lightness: 20%);
        }
      }
    }

    > .navigation__item {
      &:hover {
        > .navigation__link {
          background-color: $pforange;
          color: $white;
          text-decoration: none;
        }
      }

      .navigation__link {
        padding: 15px;
      }
    }
  }

  .navigation-level--2 {
    display: none;
    width: 100%;
    background: $pfgreen;
    flex-flow: row wrap;
    z-index: 6;
    opacity: 0%;
    pointer-events: none;
    position: absolute;
    top: 100%;
    left: 0;
    border-top: 5px solid $pforange;
    box-shadow: 0 3px 5px rgb(0 0 0 / 30%);

    &.show {
      position: relative;
      display: flex;
      top: auto;
      left: auto;
      opacity: 100%;
    }

    .navigation__item {
      display: flex;
      width: 50%;
      box-sizing: border-box;
      align-self: flex-start;

      @include respond-to-max("lg") {
        width: 100%;
      }

      .navigation__link {
        display: flex;
        justify-content: space-between;
        box-sizing: border-box;
        color: $white;
        padding-top: 10px;
        padding-bottom: 10px;
        transition:
          background ease 0.3s,
          border-left ease 0.3s;
        border-color: $pfgreen;
        font-size: 0.875rem;

        &:hover {
          text-decoration: none;
          background-color: $pforangeAlt;
          border-left: 10px solid color.adjust($pforangeAlt, $lightness: -5%);
        }
      }
    }
  }

  .navigation-level--stacked {
    flex-direction: column;
    width: 250px;
    left: auto;
    right: 0;

    @include respond-to-max("lg") {
      width: 135px;
    }

    .navigation__item {
      width: 100%;
    }
  }

  .navigation__main {
    @include respond-to-max("lg") {
      position: fixed;
      width: 100%;
      left: -100%;
      height: 100%;
      padding-top: 50px;
      background: $white;
      z-index: 0;
      transition: 0.3s all ease-in-out;
      overflow: scroll;

      &.active {
        left: 0;
      }

      ul {
        flex-direction: column;
        justify-content: center;
      }

      @include respond-to-max("sm") {
        padding-top: 20px;
        height: 90%;
      }
    }
  }

  .navigation__mobile {
    display: none;
    margin-right: 20px;

    @include respond-to-max("lg") {
      display: block;
    }

    @include respond-to-max("sm") {
      margin-right: 5px;
    }
  }

  .navigation__mobile--link {
    &.active {
      .navigation__icon {
        background: white;

        &::before {
          transform: rotate(-135deg);
          top: 0;
        }

        &::after {
          transform: rotate(135deg);
          top: 0;
        }
      }
    }
  }

  .navigation__icon {
    position: relative;
    display: block;
    height: 3px;
    width: 25px;
    background: $pfgreen;
    transform-origin: center;
    transition: 0.3s all ease-in-out;

    &::before,
    &::after {
      position: absolute;
      content: "";
      height: 3px;
      width: 25px;
      background: $pfgreen;
      display: block;
      transition: 0.3s all ease-in-out;
    }

    &::before {
      top: -7px;
    }

    &::after {
      top: 7px;
    }
  }

  .navigation__item--children {
    @include respond-to-max("lg") {
      display: flex;
      flex-flow: row wrap;

      > .navigation__link {
        width: 85%;
      }
    }
  }

  .navigation__link--children {
    position: relative;
    display: flex;
    justify-content: flex-start;
  }

  .navigation__link--icon {
    display: none;
    z-index: 100;
    top: 0;
    right: 0;
    width: 15%;
    height: 100%;
    font-size: 1.6rem;
    overflow: hidden;

    * {
      position: relative;
      top: 8px;
      right: -30%;
    }

    @include respond-to-max("lg") {
      display: block;
    }

    @include respond-to-max("sm") {
      right: 15px;
    }
  }

  body {
    &.active {
      overflow: hidden;
    }
  }
</style>
